import { api } from "@/lib/api";

// Coordinate the existing fire-and-forget POST /sessions call with the
// Preparation screen's first prep-status poll. Upload.jsx intentionally
// navigates to /prepare/:sessionId immediately after starting the POST so the
// candidate sees progress without waiting for question generation. The backend
// now returns quickly after it has inserted the placeholder session and launched
// background preparation, but the first prep-status GET can otherwise race that
// POST and see a transient 404 (or hide a real POST failure).
//
// Keep this coordination at the shared API layer rather than patching rendered
// DOM or changing interview logic. A prep-status GET for a newly-created session
// waits only for the matching POST /sessions request to settle. Once the POST
// succeeds, polling proceeds normally. If the POST fails, the original Axios
// error is propagated to Prepare.jsx so its existing error UI shows the real
// HTTP failure instead of an indefinite 404/spinner.

const pendingSessionCreates = new Map();

function sessionIdFromCreateConfig(config) {
  const url = String(config?.url || "");
  const method = String(config?.method || "get").toLowerCase();
  if (url !== "/sessions" || method !== "post") return "";

  const data = config?.data;
  if (!data || typeof data.get !== "function") return "";
  return String(data.get("session_id_override") || "").trim();
}

function prepSessionIdFromConfig(config) {
  const url = String(config?.url || "");
  const method = String(config?.method || "get").toLowerCase();
  if (method !== "get") return "";
  const match = url.match(/^\/sessions\/([^/]+)\/prep-status$/);
  return match?.[1] || "";
}

function makeDeferred() {
  let resolve;
  let reject;
  const promise = new Promise((res, rej) => {
    resolve = res;
    reject = rej;
  });
  // The POST is also observed by Upload.jsx. Mark this promise handled here so
  // a failed detached POST cannot generate an unhandled-rejection warning while
  // Prepare.jsx receives the same failure through its gated GET.
  promise.catch(() => {});
  return { promise, resolve, reject };
}

api.interceptors.request.use(async (config) => {
  const createSessionId = sessionIdFromCreateConfig(config);
  if (createSessionId) {
    let entry = pendingSessionCreates.get(createSessionId);
    if (!entry) {
      entry = { ...makeDeferred(), settled: false };
      pendingSessionCreates.set(createSessionId, entry);
    }
    config.__sessionCreationGateId = createSessionId;
    return config;
  }

  const prepSessionId = prepSessionIdFromConfig(config);
  if (prepSessionId) {
    const entry = pendingSessionCreates.get(prepSessionId);
    if (entry) {
      // Do not send prep-status until POST /sessions has either succeeded or
      // failed. Success guarantees the placeholder exists; failure propagates
      // the real creation error to Prepare.jsx.
      await entry.promise;
    }
  }

  return config;
});

api.interceptors.response.use(
  (response) => {
    const sessionId = response?.config?.__sessionCreationGateId;
    if (sessionId) {
      const entry = pendingSessionCreates.get(sessionId);
      if (entry && !entry.settled) {
        entry.settled = true;
        entry.resolve(response);
        // Retain briefly so a Prepare component mounted immediately after the
        // response can still observe the resolved creation request.
        window.setTimeout(() => pendingSessionCreates.delete(sessionId), 30_000);
      }
    }
    return response;
  },
  (error) => {
    const sessionId = error?.config?.__sessionCreationGateId;
    if (sessionId) {
      const entry = pendingSessionCreates.get(sessionId);
      if (entry && !entry.settled) {
        entry.settled = true;
        entry.reject(error);
        window.setTimeout(() => pendingSessionCreates.delete(sessionId), 30_000);
      }
    }
    return Promise.reject(error);
  },
);
